import React, { useState } from "react";
import { Container, Row, Col, Button, Card, Badge } from "react-bootstrap";
import { Link } from "react-router-dom";
import { useFadeIn } from "../App";
import ShapeDivider from "../components/ShapeDivider";
import ImageModal from "../components/ImageModal";
import SafeVideo from "../components/SafeVideo";
import { pageImageAllocations, getImageById } from "../data/wildlifeGallery";

const About = () => {
  const [heroRef, heroVisible] = useFadeIn();
  const [missionRef, missionVisible] = useFadeIn();
  const [historyRef, historyVisible] = useFadeIn();
  const [valuesRef, valuesVisible] = useFadeIn();
  const [videosRef, videosVisible] = useFadeIn();
  const [galleryRef, galleryVisible] = useFadeIn();
  const [whatWeDoRef, whatWeDoVisible] = useFadeIn();
  const [ctaRef, ctaVisible] = useFadeIn();
  const [boardRef, boardVisible] = useFadeIn();

  // Modal state for image viewing
  const [showModal, setShowModal] = useState(false);
  const [modalImage, setModalImage] = useState({ url: '', alt: '' });

  const openImageModal = (imageUrl, imageAlt) => {
    setModalImage({ url: imageUrl, alt: imageAlt });
    setShowModal(true);
  };

  // Board members data
  const boardMembers = [
    {
      name: "Jenni Abbott",
      role: "Founder and Executive Director",
      description: "Our trailblazing Founder and Executive Director, whose passion for wildlife is the driving force behind our mission.",
      image: "https://cdn.builder.io/api/v1/image/assets%2F35ffb80beae24a4483ace4ab1c92b206%2F35d55aceac2c4cc58342e05767e52e26?format=webp&width=2560&quality=95",
    },
    {
      name: "Jemma Laing, MAICD",
      role: "Chair",
      description: "Our Chair, leading with insight and fervor to ensure our Australian wildlife thrives for generations.",
      image: "https://cdn.builder.io/api/v1/image/assets%2F35ffb80beae24a4483ace4ab1c92b206%2F3401e7891389488b86c087789edda917?format=webp&width=2560&quality=95",
    },
    {
      name: "Caroline Tierney",
      role: "Treasurer",
      description: "Our Treasurer, whose keen eye keeps our conservation goals on a sustainable path.",
      image: "https://cdn.builder.io/api/v1/image/assets%2F35ffb80beae24a4483ace4ab1c92b206%2Fd7b7334a083c434a97816f56e9cd034d?format=webp&width=2560&quality=95",
    },
    {
      name: "Emma Senior",
      role: "Secretary",
      description: "Our Secretary, whose legal expertise ensures our governance framework remains strong and compliant.",
      image: "https://cdn.builder.io/api/v1/image/assets%2F35ffb80beae24a4483ace4ab1c92b206%2F18686b6869b9406bb84b1366df04e506?format=webp&width=2560&quality=95",
    },
    {
      name: "Devon Cuimara",
      role: "Board Member",
      description: "A Board Member whose deep connection to the land enriches our direction and purpose.",
      image: "https://cdn.builder.io/api/v1/image/assets%2F35ffb80beae24a4483ace4ab1c92b206%2F40f6e63257994b43bbd2e784e5bad67a?format=webp&width=2560&quality=95",
    },
    {
      name: "Julie Ferguson",
      role: "Board Member",
      description: "Also a Board Member, whose creative energy infuses innovation into our conservation strategies.",
      image: "https://cdn.builder.io/api/v1/image/assets%2F35ffb80beae24a4483ace4ab1c92b206%2Ffc2c880c56374e95b3d31cd8d42fb558?format=webp&width=2560&quality=95",
    },
  ];

  return (
    <>
      {/* Hero Section */}
      <section
        ref={heroRef}
        className={`hero-about-section fade-in-section ${heroVisible ? "is-visible" : ""}`}
        style={{
          backgroundImage: `linear-gradient(rgba(74, 92, 45, 0.8), rgba(141, 191, 75, 0.8)), url(https://cdn.builder.io/api/v1/image/assets%2F35ffb80beae24a4483ace4ab1c92b206%2F7ed7e3a7b7054fe7a2df2c0a8ba965f2?format=webp&width=2560&quality=95)`,
          padding: "120px 0 80px",
          color: "white",
          position: "relative",
          overflow: "hidden",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundAttachment: "fixed"
        }}
      >
        <div
          className="hero-pattern"
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage:
              'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.05"%3E%3Ccircle cx="30" cy="30" r="2"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
            opacity: 0.3,
          }}
        ></div>
        <Container className="position-relative">
          <Row className="align-items-center">
            <Col lg={8} className="mx-auto text-center">
              <h1
                className="display-3 fw-bold mb-4 d-md-inline d-block"
                style={{
                  color: "white",
                  whiteSpace: "normal"
                }}
              >
                <span className="d-md-inline d-block">About Aussie</span>{" "}
                <span className="d-md-inline d-block">Wildlife Limited</span>
              </h1>
              <p
                className="lead mb-4"
                style={{ fontSize: "1.3rem", opacity: 0.9 }}
              >
                Born from a fierce determination to provide sustainable care for
                Australia's wildlife
              </p>
              <div className="d-flex justify-content-center gap-3 flex-wrap">
                <Button
                  as={Link}
                  to="/team"
                  variant="light"
                  size="lg"
                  className="px-4"
                >
                  Meet Our Team
                </Button>
                <Button
                  as={Link}
                  to="/contact"
                  variant="outline-light"
                  size="lg"
                  className="px-4"
                >
                  Get In Touch
                </Button>
              </div>
            </Col>
          </Row>
        </Container>
        <ShapeDivider color="white" />
      </section>

      {/* Mission Section */}
      <section
        ref={missionRef}
        className={`mission-section fade-in-section ${missionVisible ? "is-visible" : ""}`}
        style={{ padding: "100px 0" }}
      >
        <Container>
          <Row className="align-items-center g-5">
            <Col lg={6}>
              <div className="mission-content">
                <h2
                  className="mb-4"
                  style={{ color: "var(--primary-green)", fontSize: "2.5rem" }}
                >
                  Our Mission
                </h2>
                <p
                  className="lead mb-4"
                  style={{ fontSize: "1.2rem", lineHeight: "1.8" }}
                >
                  We are on a mission to rehabilitate distressed wildlife and
                  promote sustainable conservation practices for the
                  preservation of natural habitats.
                </p>
                <p
                  style={{
                    fontSize: "1.1rem",
                    lineHeight: "1.7",
                    color: "var(--dark-text)",
                  }}
                >
                  We believe in telling regenerative stories that educate and
                  inspire action, fostering a culture of ecological regeneration
                  led by communities. Our vision is a world where all living
                  beings thrive and humans play a vital role in a healthy and
                  diverse ecosystem.
                </p>
              </div>
            </Col>
            <Col lg={6}>
              <div className="mission-image-container position-relative">
                <img
                  src={getImageById(pageImageAllocations.about.mission).url}
                  alt={getImageById(pageImageAllocations.about.mission).alt}
                  className="img-fluid rounded-3 shadow-lg"
                  style={{ width: "100%", height: "700px", objectFit: "cover" }}
                />
                <div
                  className="image-overlay position-absolute"
                  style={{
                    top: "20px",
                    right: "20px",
                    background: "rgba(255,255,255,0.3)",
                    backdropFilter: "blur(10px)",
                    padding: "1rem",
                    borderRadius: "10px",
                    boxShadow: "0 5px 20px rgba(0,0,0,0.1)",
                  }}
                >
                  <h6
                    className="mb-1 fw-bold"
                    style={{ color: "var(--primary-green)" }}
                  >
                    10+ Years
                  </h6>
                  <small className="text-muted">
                    Experience in Wildlife Care
                  </small>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* History Section */}
      <section
        ref={historyRef}
        className={`history-section section-bg fade-in-section ${historyVisible ? "is-visible" : ""}`}
        style={{ padding: "100px 0" }}
      >
        <Container>
          <Row className="align-items-center g-5">
            <Col lg={6} className="order-lg-2">
              <div className="history-content">
                <h2
                  className="mb-4"
                  style={{ color: "var(--primary-green)", fontSize: "2.5rem" }}
                >
                  Our History
                </h2>
                <p
                  className="lead mb-4"
                  style={{ fontSize: "1.2rem", lineHeight: "1.8" }}
                >
                  Born out of a determined carer's vision, Aussie Wildlife
                  Limited emerged to provide sustainable care for Australia's
                  wildlife.
                </p>
                <p
                  style={{
                    fontSize: "1.1rem",
                    lineHeight: "1.7",
                    color: "var(--dark-text)",
                  }}
                >
                  With six years of hands-on experience, this organisation
                  addresses the challenges faced in wildlife care and advocates
                  for a more sustainable approach. Supported by a diverse board
                  of directors with backgrounds in wildlife care, health,
                  business, and finance, we develop innovative strategies for
                  promoting ethical and sustainable practices.
                </p>
                <p
                  style={{
                    fontSize: "1.1rem",
                    lineHeight: "1.7",
                    color: "var(--dark-text)",
                  }}
                >
                  Together, we strive to make a meaningful difference in the
                  lives of Australia's wildlife and create a better future for
                  all.
                </p>
              </div>
            </Col>
            <Col lg={6} className="order-lg-1">
              <div className="history-image-container">
                <img
                  src={getImageById(pageImageAllocations.about.history).url}
                  alt={getImageById(pageImageAllocations.about.history).alt}
                  className="img-fluid rounded-3 shadow-lg"
                  style={{ width: "100%", height: "400px", objectFit: "cover" }}
                />
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Core Values Section */}
      <section
        ref={valuesRef}
        className={`values-section fade-in-section ${valuesVisible ? "is-visible" : ""}`}
        style={{ padding: "100px 0" }}
      >
        <Container>
          <Row className="text-center mb-5">
            <Col lg={8} className="mx-auto">
              <h2
                className="mb-4"
                style={{ color: "var(--primary-green)", fontSize: "2.5rem" }}
              >
                Our Core Values
              </h2>
              <p
                className="lead"
                style={{ fontSize: "1.2rem", color: "var(--dark-text)" }}
              >
                These principles guide everything we do in our mission to
                protect and nurture Australia's wildlife
              </p>
            </Col>
          </Row>
          <Row className="g-4">
            {[
              {
                icon: "fa-heart",
                title: "Care",
                description:
                  "Providing compassionate, ethical care for every animal that comes into our rehabilitation network",
                imageId: pageImageAllocations.about.values[0]
              },
              {
                icon: "fa-leaf",
                title: "Environment",
                description:
                  "Protecting and preserving natural habitats while promoting sustainable conservation practices",
                imageId: pageImageAllocations.about.values[1]
              },
              {
                icon: "fa-seedling",
                title: "Natural Foods",
                description:
                  "Ensuring proper nutrition with species-appropriate diets that support healthy recovery and development",
                imageId: pageImageAllocations.about.values[2]
              },
              {
                icon: "fa-users",
                title: "Buddies",
                description:
                  "Building strong community networks and partnerships to maximise our conservation impact",
                imageId: pageImageAllocations.about.values[3]
              },
            ].map((value, index) => {
              const valueImage = getImageById(value.imageId);
              return (
                <Col md={6} lg={3} key={index}>
                  <Card
                    className="h-100 border-0 shadow-lg"
                    style={{
                      borderRadius: "20px",
                      transition: "transform 0.3s ease, box-shadow 0.3s ease",
                      overflow: "hidden"
                    }}
                  >
                    <div
                      className="value-image-header"
                      style={{
                        height: "280px",
                        backgroundImage: `linear-gradient(rgba(74, 92, 45, 0.3), rgba(205, 164, 52, 0.3)), url(${valueImage.url})`,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                        backgroundRepeat: "no-repeat",
                        backgroundColor: "#f8f9fa",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center"
                      }}
                    >
                      <div
                        className="value-icon"
                        style={{
                          width: "60px",
                          height: "60px",
                          background: "rgba(255, 255, 255, 0.9)",
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "var(--primary-green)",
                          fontSize: "1.5rem",
                          boxShadow: "0 5px 20px rgba(0, 0, 0, 0.2)",
                        }}
                      >
                        <i className={`fas ${value.icon}`}></i>
                      </div>
                    </div>
                    <Card.Body className="text-center p-4">
                      <h4
                        className="fw-bold mb-3"
                        style={{ color: "var(--primary-green)" }}
                      >
                        {value.title}
                      </h4>
                      <p className="text-muted" style={{ lineHeight: "1.6" }}>
                        {value.description}
                      </p>
                    </Card.Body>
                  </Card>
                </Col>
              );
            })}
          </Row>
        </Container>
      </section>

      {/* Our Mission in Action Videos */}
      <section
        ref={videosRef}
        className={`mission-videos-section fade-in-section ${videosVisible ? "is-visible" : ""}`}
        style={{ padding: "100px 0", background: "white" }}
      >
        <Container>
          <Row className="text-center mb-5">
            <Col lg={8} className="mx-auto">
              <h2
                className="mb-4"
                style={{ color: "var(--primary-green)", fontSize: "2.5rem" }}
              >
                Our Mission in Action
              </h2>
              <p
                className="lead"
                style={{ fontSize: "1.2rem", color: "var(--dark-text)" }}
              >
                Experience the passion and dedication that drives our conservation mission -
                every day we're working to make a difference for Australia's wildlife
              </p>
            </Col>
          </Row>
          <Row className="g-5 justify-content-center">
            {[
              "https://cdn.builder.io/o/assets%2F35ffb80beae24a4483ace4ab1c92b206%2F5d965a55e9f44984a49e038bc1e076da?alt=media&token=2057b9b6-5d0b-456c-ae3e-2e6664d7ab4d&apiKey=35ffb80beae24a4483ace4ab1c92b206",
              "https://cdn.builder.io/o/assets%2F35ffb80beae24a4483ace4ab1c92b206%2Ffecbf905a2804026a724a7818c2cc77c?alt=media&token=4091186b-03b7-4095-aeb2-135c25312de3&apiKey=35ffb80beae24a4483ace4ab1c92b206",
              "https://cdn.builder.io/o/assets%2F35ffb80beae24a4483ace4ab1c92b206%2F41872d6eaf9140aebbc5c05663fc2a7a?alt=media&token=dd9c10fa-ab8a-478d-b8f6-ad4a130762d7&apiKey=35ffb80beae24a4483ace4ab1c92b206"
            ].map((videoUrl, index) => (
              <Col lg={4} key={index}>
                <div
                  className="video-container shadow-lg"
                  style={{
                    borderRadius: "25px",
                    overflow: "hidden",
                    transition: "transform 0.3s ease, box-shadow 0.3s ease",
                  }}
                >
                  <SafeVideo
                    src={videoUrl}
                    controls
                    className="w-100"
                    style={{ aspectRatio: "3/4", objectFit: "cover" }}
                    preload="metadata"
                    autoGeneratePoster={true}
                  />
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Wildlife Gallery Section */}
      <section
        ref={galleryRef}
        className={`wildlife-gallery-about fade-in-section ${galleryVisible ? "is-visible" : ""}`}
        style={{ padding: "100px 0", background: "var(--light-bg)" }}
      >
        <Container>
          <Row className="text-center mb-5">
            <Col lg={8} className="mx-auto">
              <h2
                className="mb-4"
                style={{ color: "var(--primary-green)", fontSize: "2.5rem" }}
              >
                Wildlife We Care For
              </h2>
              <p
                className="lead"
                style={{ fontSize: "1.2rem", color: "var(--dark-text)" }}
              >
                Meet some of the amazing animals that have been part of our rehabilitation programme
              </p>
            </Col>
          </Row>
          <Row className="g-4">
            {pageImageAllocations.about.gallery.map((imageId, index) => {
              const image = getImageById(imageId);
              return (
                <Col md={6} lg={3} key={image.id}>
                  <div
                    className="wildlife-gallery-card shadow"
                    style={{
                      borderRadius: "15px",
                      overflow: "hidden",
                      transition: "transform 0.3s ease, box-shadow 0.3s ease",
                    }}
                  >
                    <img
                      src={image.url}
                      alt={image.alt}
                      className="w-100"
                      style={{
                        height: "350px",
                        objectFit: "cover",
                        transition: "transform 0.3s ease",
                        cursor: "pointer"
                      }}
                      onClick={() => openImageModal(image.url, image.alt)}
                    />
                  </div>
                </Col>
              );
            })}
            {[
              "https://cdn.builder.io/api/v1/image/assets%2F35ffb80beae24a4483ace4ab1c92b206%2Fd93dc406fa264f12aa2202b4f0469342?format=webp&width=2560&quality=95",
              "https://cdn.builder.io/api/v1/image/assets%2F35ffb80beae24a4483ace4ab1c92b206%2F8fe6a63f3a764645a221f60064b842b9?format=webp&width=2560&quality=95",
              "https://cdn.builder.io/api/v1/image/assets%2F35ffb80beae24a4483ace4ab1c92b206%2F894164b00bd44382be7469306b78c462?format=webp&width=2560&quality=95"
            ].map((imageUrl, index) => (
              <Col md={6} lg={3} key={`new-image-${index}`}>
                <div
                  className="wildlife-gallery-card shadow"
                  style={{
                    borderRadius: "15px",
                    overflow: "hidden",
                    transition: "transform 0.3s ease, box-shadow 0.3s ease",
                  }}
                >
                  <img
                    src={imageUrl}
                    alt="Wildlife"
                    className="w-100"
                    style={{
                      height: "350px",
                      objectFit: "cover",
                      transition: "transform 0.3s ease",
                      cursor: "pointer"
                    }}
                    onClick={() => openImageModal(imageUrl, "Wildlife")}
                  />
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* What We Do Section */}
      <section
        ref={whatWeDoRef}
        className={`what-we-do-section fade-in-section ${whatWeDoVisible ? "is-visible" : ""}`}
        style={{ padding: "100px 0", background: "white" }}
      >
        <Container>
          <Row className="text-center mb-5">
            <Col lg={8} className="mx-auto">
              <h2
                className="mb-4"
                style={{ color: "var(--primary-green)", fontSize: "2.5rem" }}
              >
                What We Do
              </h2>
              <p
                className="lead"
                style={{ fontSize: "1.2rem", color: "var(--dark-text)" }}
              >
                Our comprehensive approach to wildlife conservation encompasses every aspect
                of animal care, from emergency rescue to successful release back into the wild
              </p>
            </Col>
          </Row>

          <Row className="g-4">
            {[
              {
                icon: "fa-graduation-cap",
                title: "Education",
                description:
                  "We provide comprehensive wildlife education programmes to schools, community groups, and the general public. Through hands-on workshops, informative presentations, and interactive experiences, we teach proper wildlife handling, first aid techniques, and conservation awareness. Our educational initiatives help build a knowledgeable community that can respond appropriately to wildlife encounters and support conservation efforts."
              },
              {
                icon: "fa-dove",
                title: "Releases",
                description:
                  "Our ultimate goal is the successful release of rehabilitated wildlife back into their natural habitats. We carefully assess each animal's readiness for release, ensuring they have fully recovered and possess the necessary survival skills. We select appropriate release sites that provide suitable habitat, food sources, and minimal human interference, giving our animals the best chance of thriving in the wild."
              },
              {
                icon: "fa-heart-pulse",
                title: "Rehabilitation",
                description:
                  "At the heart of our work is our comprehensive rehabilitation programme. We provide specialised medical care, proper nutrition, and appropriate housing for injured, orphaned, and distressed wildlife. Our 4-pillar approach focuses on Care, Environment, Natural Foods, and Buddies, ensuring each animal receives individualized treatment tailored to their species' specific needs and natural behaviors."
              },
              {
                icon: "fa-ambulance",
                title: "Rescues",
                description:
                  "We operate a 24/7 emergency rescue service for wildlife in distress across Queensland. Our experienced team responds to calls involving injured animals, orphaned babies, and wildlife in dangerous situations. We work closely with local authorities, veterinarians, and the community to ensure rapid response times and the highest standard of emergency care for native Australian wildlife."
              },
              {
                icon: "fa-users",
                title: "Community Engagement",
                description:
                  "Building strong community connections is essential to our conservation mission. We actively engage with local residents, businesses, and organisations to create a network of wildlife advocates. Through community events, social media outreach, and partnership programmes, we foster awareness about wildlife issues and encourage community participation in conservation efforts."
              },
              {
                icon: "fa-hands-helping",
                title: "Volunteers",
                description:
                  "Our dedicated volunteers are the backbone of our organisation. We provide comprehensive training programmes for wildlife carers, transporters, and support staff. Volunteers assist with daily animal care, emergency rescues, community education events, and fundraising activities. We create a supportive network where experienced carers mentor newcomers, ensuring high standards of animal care and welfare."
              },
              {
                icon: "fa-heart",
                title: "Community Donations",
                description:
                  "As a community-funded organisation, we rely entirely on the generous support of individuals, businesses, and grant providers. Donations fund essential supplies like specialised food, medical equipment, housing materials, and transportation costs. Every contribution, no matter the size, directly impacts our ability to save lives and support Australia's precious wildlife through their journey back to the wild."
              }
            ].map((item, index) => (
              <Col md={6} lg={4} key={index}>
                <Card
                  className="what-we-do-card h-100 border-0 shadow-lg"
                  style={{
                    borderRadius: "20px",
                    transition: "transform 0.3s ease, box-shadow 0.3s ease",
                    background: "white"
                  }}
                >
                  <Card.Body className="p-4 text-center">
                    <div
                      className="activity-icon mb-3"
                      style={{
                        width: "80px",
                        height: "80px",
                        background: "linear-gradient(135deg, var(--primary-green) 0%, var(--accent-green) 100%)",
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        margin: "0 auto",
                        color: "white",
                        fontSize: "2rem",
                        boxShadow: "0 10px 30px rgba(74, 92, 45, 0.3)"
                      }}
                    >
                      <i className={`fas ${item.icon}`}></i>
                    </div>
                    <h4
                      className="fw-bold mb-3"
                      style={{ color: "var(--primary-green)" }}
                    >
                      {item.title}
                    </h4>
                    <p
                      className="text-muted text-start"
                      style={{ fontSize: "0.95rem", lineHeight: "1.6" }}
                    >
                      {item.description}
                    </p>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Our Board Section */}
      <section
        ref={boardRef}
        className={`our-board-section fade-in-section ${boardVisible ? "is-visible" : ""}`}
        style={{ padding: "100px 0", background: "white" }}
      >
        <Container>
          <Row className="text-center mb-5">
            <Col lg={10} className="mx-auto">
              <h2
                className="mb-4"
                style={{ color: "var(--primary-green)", fontSize: "2.5rem" }}
              >
                🌿🐾 Introducing Our Stewards at Aussie Wildlife Limited 🐨💼
              </h2>
              <p
                className="lead"
                style={{ fontSize: "1.2rem", color: "var(--dark-text)", lineHeight: "1.8" }}
              >
                Let's give a big shoutout to the leaders guiding us at Aussie Wildlife Limited:
              </p>
            </Col>
          </Row>
          <Row className="g-5 justify-content-center">
            {boardMembers.map((member, index) => (
              <Col lg={4} md={6} key={index}>
                <Card
                  className="board-member-card h-100 border-0 shadow-lg"
                  style={{
                    borderRadius: "20px",
                    transition: "transform 0.3s ease, box-shadow 0.3s ease",
                  }}
                >
                  <Card.Body className="p-4 text-center">
                    <div className="board-member-image-container mb-3">
                      <img
                        src={member.image}
                        alt={`${member.name} - ${member.role}`}
                        className="img-fluid rounded-circle shadow"
                        style={{
                          width: "150px",
                          height: "150px",
                          objectFit: "cover",
                          objectPosition: "center top",
                          border: "4px solid var(--primary-yellow)",
                        }}
                      />
                    </div>
                    <h4
                      className="fw-bold mb-2"
                      style={{ color: "var(--primary-green)" }}
                    >
                      {member.name}
                    </h4>
                    <Badge
                      className="mb-3 px-3 py-2"
                      style={{
                        background: "var(--accent-green)",
                        fontSize: "0.85rem",
                        borderRadius: "50px",
                      }}
                    >
                      {member.role}
                    </Badge>
                    <p
                      className="text-muted mb-3 text-start"
                      style={{ fontSize: "0.95rem", lineHeight: "1.6" }}
                    >
                      {member.description}
                    </p>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
          <Row className="mt-5">
            <Col className="text-center">
              <p
                className="lead"
                style={{ fontSize: "1.2rem", color: "var(--dark-text)", lineHeight: "1.8" }}
              >
                This team is the heart of Aussie Wildlife Limited, each bringing their unique expertise to champion the cause of Australia's precious ecosystems. Join us as we embark on this vital journey together! 🌳🦜🎉
              </p>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Call to Action Section */}
      <section
        ref={ctaRef}
        className={`cta-about-section wildlife-bg-section fade-in-section ${ctaVisible ? "is-visible" : ""}`}
        style={{
          backgroundImage: `linear-gradient(rgba(74, 92, 45, 0.85), rgba(74, 92, 45, 0.85)), url(${getImageById('lorikeet-detail').url})`,
          padding: "80px 0",
          color: "white",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundAttachment: "fixed"
        }}
      >
        <Container className="text-center">
          <Row className="justify-content-center">
            <Col lg={8}>
              <h2
                className="mb-4"
                style={{ color: "white", fontSize: "2.5rem" }}
              >
                Join Our Mission
              </h2>
              <p
                className="lead mb-4"
                style={{ fontSize: "1.2rem", opacity: 0.9 }}
              >
                Whether you want to volunteer, learn more about our work, or
                support our cause, there are many ways to get involved in
                protecting Australia's wildlife.
              </p>
              <div className="d-flex justify-content-center gap-3 flex-wrap">
                <Button
                  as={Link}
                  to="/contact"
                  variant="light"
                  size="lg"
                  className="px-4"
                  style={{ borderRadius: "50px", fontWeight: "600" }}
                >
                  Get Involved
                </Button>
                <Button
                  as={Link}
                  to="/what-we-do"
                  variant="outline-light"
                  size="lg"
                  className="px-4"
                  style={{ borderRadius: "50px", fontWeight: "600" }}
                >
                  Learn More
                </Button>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Image Modal */}
      <ImageModal
        show={showModal}
        onHide={() => setShowModal(false)}
        imageUrl={modalImage.url}
        imageAlt={modalImage.alt}
      />
    </>
  );
};

export default About;
